import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Header from "../../../component/organisms/header";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import Sidebar from "../../../component/organisms/sidebar";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import { useLocation, useNavigate } from "react-router-dom";
import PretendardText from "../../../component/atoms/text/pretendardText";
import TableText from "./../../../component/atoms/text/table/tableText";
import { route } from "../../../router/route";
import scrollToTop from "../../../util/scrollToTop";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
  ComposedChart,
  ResponsiveContainer,
} from "recharts";
import HomeCustomTooltip from "./_components/homeCustomTooltip";
import HomeCustomTick from "./_components/homeCustomTick";
import AnalyticsApi from "../../../api/analytics.api";
import moment from "moment";
import { errorHandler } from "../../../util/errorHandler";
import getToday from "../../../util/getToday";
import clientPageData from "../../../constants/clientPageData";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import Loading from "../../../component/templates/Loading";
import { minToMs } from "../../../util/formatMs";

const StatisticsHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;

  // ========== 매출 기간별 분석 관련 ========== //
  const [revenueList, setRevenueList] = useState([]);
  const [periodAnalysisList, setPeriodAnalysisList] = useState([]);
  const [periodAnalysisWeekList, setPeriodAnalysisWeekList] = useState([]);

  const getPeriodAnalysisList = async () => {
    try {
      const today = getToday();

      const data = {
        page: 1,
        size: 0,
        searchDate: today,
        periodType: "day",
      };

      const response = (await AnalyticsApi.getListAnalytics(data)).data.data
        .content;

      return response || [];
    } catch (error) {
      errorHandler(error);
    }
  };

  const {
    data: periodData = [],
    isLoading: isPeriodLoading,
    isError: isPeriodError,
  } = useQuery({
    queryKey: ["periodData", pathName],
    queryFn: getPeriodAnalysisList,
    staleTime: 1000 * 60 * 15, // 15분 캐시 유지
  });

  useEffect(() => {
    if (periodData?.length > 0) {
      // 매출 (10일치)
      const revenueProcessedResponse = periodData.slice(0, 10);
      const revenueProcessedResponse2 = revenueProcessedResponse.map((item) => {
        return {
          date: moment(item.createdAt).format("YYYY-MM-DD"),
          revenue: Number(item.revenue),
          storeSalesCount: item.storeSalesCount,
        };
      });

      const reversedRevenueList = [...revenueProcessedResponse2].reverse();
      setRevenueList(reversedRevenueList);

      // 기간별 분석 리스트(일주일)
      const periodWeekProcessedResponse = periodData.slice(0, 7);

      setPeriodAnalysisWeekList(periodWeekProcessedResponse);

      // 기간별 분석 리스트
      setPeriodAnalysisList(periodData);
    }
  }, [periodData]);

  // ========== 많이 방문한 페이지 관련 ========== //
  const [mostVisitedData, setMostVisitedData] = useState([]);
  const today = useMemo(() => getToday(), []);

  const getMostVisitedData = async () => {
    const data = {
      endDate: today,
      startDate: moment(today).subtract(9, "days").format("YYYY-MM-DD"),
    };

    const response = (await AnalyticsApi.getListMostVisited(data)).data;

    return response || [];
  };

  const {
    data: mostVisitData = [],
    isLoading: isMostVisitLoading,
    isError: isMostVisitError,
  } = useQuery({
    enabled: false, // 비활성화 (임시 코드이고 나중에 삭제해야함)
    queryKey: ["mostVisitData", pathName],
    queryFn: getMostVisitedData,
    onError: (error) => {
      errorHandler(error);
      toast(
        <>
          많이 방문한 페이지 데이터를 불러오는 중 오류가 발생했습니다.
          <br />
          구글 애널리틱스에서 확인해주세요.
        </>
      );
    },
    staleTime: minToMs(15),
  });

  useEffect(() => {
    if (mostVisitData?.length > 0) {
      const processedData = mostVisitData?.map((item) => {
        const path = item.pagePath;

        if (path === "/") {
          const homePage = clientPageData.find((page) => page.path === "/");
          return {
            ...item,
            pagePath: path,
            title: homePage ? homePage.title : "홈",
          };
        }

        const matchedPage = clientPageData.find((page) =>
          path.includes(page.path)
        );

        return {
          ...item,
          pagePath: path,
          title: matchedPage ? matchedPage.title : "Unknown",
        };
      });

      // 최대 5개까지만 보여주기
      const slicedData = processedData.slice(0, 5);

      setMostVisitedData(slicedData);
    }
  }, [mostVisitData]);

  // 유입 사이트 랜더링 여부 (임시 코드이고 나중에 삭제해야함)
  const [isShowInflow] = useState(false);

  // 많이 방문한 페이지 랜더링 여부 (임시 코드이고 나중에 삭제해야함)
  const [isShowMostVisited] = useState(false);

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>통계 홈</TableTitle>
          <ContentContainer>
            {/* ********** 매출 ********** */}
            <ChartContainer>
              <ChartHeader>
                <ChartHeaderText>매출</ChartHeaderText>
              </ChartHeader>
              {isPeriodLoading || isPeriodError ? (
                <div
                  style={{
                    width: "100%",
                    height: 327,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <>
                  <ChartGraphWrapper>
                    <div
                      style={{
                        width: "fit-content",
                        height: 23,
                        display: "flex",
                        alignItems: "center",
                        gap: 20,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            backgroundColor: "#b658ff",
                            borderRadius: "50%",
                          }}
                        />
                        <PretendardText
                          style={{
                            color: "#aaa",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "22.75px",
                          }}
                        >
                          매출 금액
                        </PretendardText>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            backgroundColor: "#34f3ff",
                            borderRadius: "50%",
                          }}
                        />
                        <PretendardText
                          style={{
                            color: "#aaa",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "22.75px",
                          }}
                        >
                          구매 건수
                        </PretendardText>
                      </div>
                    </div>
                  </ChartGraphWrapper>
                  <div
                    style={{
                      width: "100%",
                      height: "fit-content",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "51px 0px 30px 0px",
                    }}
                  >
                    <ResponsiveContainer width={939} height={256}>
                      <ComposedChart
                        data={revenueList}
                        margin={{ top: 30, right: 30, left: 30, bottom: 20 }}
                      >
                        <CartesianGrid
                          stroke="rgba(255, 255, 255, 0.12)"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="date"
                          axisLine={false}
                          stroke="rgba(255, 255, 255, 0.12)"
                          tick={({ x, y, payload, index }) => (
                            <HomeCustomTick
                              x={x}
                              y={y}
                              payload={payload}
                              index={index}
                              data={revenueList}
                            />
                          )}
                          tickMargin={22}
                          interval={0}
                        />
                        <YAxis
                          yAxisId="left"
                          orientation="left"
                          axisLine={false}
                          stroke="rgba(255, 255, 255, 0.12)"
                          width={76}
                          minTickGap={20}
                          tickFormatter={(value) =>
                            formatNumberWithCommas(value)
                          }
                          tickSize={0}
                          tickMargin={20} // 숫자와 축 사이의 간격 조정
                          tick={{
                            fontFamily: "Pretendard",
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: "18.75px",
                            fill: "#aaa",
                            textAnchor: "end",
                          }}
                          // domain={[
                          //   0,
                          //   Math.max(
                          //     ...revenueList.map((item) => Number(item.revenue))
                          //   ) * 1.2,
                          // ]}
                        >
                          <Label
                            value="(원)"
                            position="insideBottom" // "insideBottomRight", "insideBottom", "insideBottomLeft", "insideTopRight", "insideTop", "insideTopLeft"
                            offset={-26} // Y축에 가까이 붙게 하려면 offset 값을 음수로 설정
                            dx={10}
                            style={{
                              fontFamily: "Pretendard",
                              fontWeight: 500,
                              fontSize: 13,
                              lineHeight: "18.75px",
                              fill: "rgba(181, 181, 181, 0.5)",
                            }}
                          />
                        </YAxis>
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          axisLine={false}
                          stroke="rgba(255, 255, 255, 0.12)"
                          tickSize={0}
                          tickFormatter={(value) =>
                            formatNumberWithCommas(value)
                          }
                          tickMargin={20} // 숫자와 축 사이의 간격 조정
                          width={44}
                          tick={{
                            fontFamily: "Pretendard",
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: "18.75px",
                            fill: "#aaa",
                            textAnchor: "start",
                          }}
                        >
                          <Label
                            value="(건)"
                            position="insideBottom" // "insideBottomRight", "insideBottom", "insideBottomLeft", "insideTopRight", "insideTop", "insideTopLeft"
                            offset={-26} // Y축에 가까이 붙게 하려면 offset 값을 음수로 설정
                            dx={5}
                            style={{
                              fontFamily: "Pretendard",
                              fontWeight: 500,
                              fontSize: 13,
                              lineHeight: "18.75px",
                              fill: "rgba(181, 181, 181, 0.5)",
                            }}
                          />
                        </YAxis>
                        <Tooltip content={<HomeCustomTooltip />} />
                        <Bar
                          yAxisId="left"
                          dataKey="revenue"
                          fill="#b658ff"
                          radius={[5, 5, 0, 0]}
                          barSize={14}
                        />
                        <Line
                          yAxisId="right"
                          type="linear"
                          dataKey="storeSalesCount"
                          stroke="#34f3ff"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </ChartContainer>

            {/* ********** 기간별 분석 ********** */}
            <ChartContainer>
              <ChartHeader>
                <ChartHeaderText>기간별 분석</ChartHeaderText>
                <PretendardText
                  style={{
                    fontWeight: 500,
                    fontSize: "13px",
                    color: "#aaa",
                    lineHeight: "18.75px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    navigate(route.statistics_period);
                    scrollToTop();
                  }}
                >
                  더보기
                </PretendardText>
              </ChartHeader>
              {isPeriodLoading || isPeriodError ? (
                <div
                  style={{
                    width: "100%",
                    height: 572,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <>
                  {/* 테이블 영역 */}
                  <table
                    style={{
                      width: "100%",
                      borderSpacing: 0,
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      {/* 합계: 1044px */}
                      {/* 일자 */}
                      <col width="111px" />
                      {/* 구매수 */}
                      <col width="96px" />
                      {/* 매출액 */}
                      <col width="151px" />
                      {/* 페이지뷰 */}
                      <col width="108px" />
                      {/* 방문자 */}
                      <col width="97px" />
                      {/* 가입 */}
                      <col width="77px" />
                      {/* 멤버십 */}
                      <col width="77px" />
                      {/* 문의 */}
                      <col width="68px" />
                      {/* 후기 */}
                      <col width="74px" />
                      {/* 새글 */}
                      <col width="66px" />
                      {/* 댓글 */}
                      <col width="119px" />
                    </colgroup>
                    <thead>
                      <tr
                        style={{
                          height: 41,
                          backgroundColor: "#313233",
                        }}
                      >
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              paddingLeft: 30,
                            }}
                          >
                            일자
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            구매수
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            매출액
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            페이지뷰
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            방문자
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            가입
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            멤버십
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            문의
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            후기
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            새글
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                              paddingRight: 53,
                            }}
                          >
                            댓글
                          </TableText>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodAnalysisWeekList.map((item, index) => {
                        return (
                          <>
                            <tr
                              key={index}
                              style={{
                                height: 59,
                                borderBottom:
                                  "0.5px solid rgba(255, 255, 255, 0.12)",
                              }}
                            >
                              {/* 일자 */}
                              <td>
                                <TableText
                                  style={{
                                    paddingLeft: 30,
                                  }}
                                >
                                  {moment(item.createdAt).format("YYYY-MM-DD")}
                                </TableText>
                              </td>
                              {/* 구매수 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.storeSalesCount || 0
                                  )}
                                </TableText>
                              </td>
                              {/* 매출액 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(item?.revenue || 0)}
                                </TableText>
                              </td>
                              {/* 페이지뷰 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.pageViewCount || 0
                                  )}
                                </TableText>
                              </td>
                              {/* 방문자 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.visitCount || 0
                                  )}
                                </TableText>
                              </td>
                              {/* 가입 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.signupCount || 0
                                  )}
                                </TableText>
                              </td>
                              {/* 멤버십 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    (item?.annualCount || 0) +
                                      (item?.lifetimeCount || 0)
                                  )}
                                </TableText>
                              </td>
                              {/* 문의 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(item?.askCount || 0)}
                                </TableText>
                              </td>
                              {/* 후기 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.reviewCount || 0
                                  )}
                                </TableText>
                              </td>
                              {/* 새글 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.boardPostCount || 0
                                  )}
                                </TableText>
                              </td>
                              {/* 댓글 */}
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                    marginRight: 53,
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    item?.commentCount || 0
                                  )}
                                </TableText>
                              </td>
                            </tr>
                          </>
                        );
                      })}
                      {/* 최근 7일 합계 */}
                      <tr
                        style={{
                          height: 59,
                          borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        {/* 최근 7일 합계 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              paddingLeft: 30,
                            }}
                          >
                            최근 7일 합계
                          </TableText>
                        </td>
                        {/* 구매수 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.storeSalesCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 매출액 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + Number(cur.revenue),
                                0
                              )
                            )}
                            원
                          </TableText>
                        </td>
                        {/* 페이지뷰 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + Number(cur.pageViewCount),
                                0
                              )
                            )}
                            회
                          </TableText>
                        </td>
                        {/* 방문자 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.visitCount,
                                0
                              )
                            )}
                            명
                          </TableText>
                        </td>
                        {/* 가입 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.signupCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 멤버십 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.lifetimeCount,
                                0
                              ) +
                                periodAnalysisWeekList.reduce(
                                  (acc, cur) => acc + cur.annualCount,
                                  0
                                )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 문의 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.askCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 후기 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.reviewCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 새글 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.boardPostCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 댓글 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                              marginRight: 53,
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisWeekList.reduce(
                                (acc, cur) => acc + cur.commentCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                      </tr>
                      {/* 이번달 합계 */}
                      <tr
                        style={{
                          height: 59,
                        }}
                      >
                        {/* 이번달 합계 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              paddingLeft: 30,
                            }}
                          >
                            이번달 합계
                          </TableText>
                        </td>
                        {/* 구매수 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.storeSalesCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 매출액 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + Number(cur.revenue),
                                0
                              )
                            )}
                            원
                          </TableText>
                        </td>
                        {/* 페이지뷰 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + Number(cur.pageViewCount),
                                0
                              )
                            )}
                            회
                          </TableText>
                        </td>
                        {/* 방문자 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.visitCount,
                                0
                              )
                            )}
                            명
                          </TableText>
                        </td>
                        {/* 가입 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.signupCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 멤버십 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.lifetimeCount,
                                0
                              ) +
                                periodAnalysisList.reduce(
                                  (acc, cur) => acc + cur.annualCount,
                                  0
                                )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 문의 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.askCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 후기 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.reviewCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 새글 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.boardPostCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                        {/* 댓글 */}
                        <td>
                          <TableText
                            style={{
                              fontWeight: "600",
                              letterSpacing: 0,
                              lineHeight: "22.75px",
                              color: "#f3f3f3",
                              textAlign: "right",
                              marginRight: 53,
                            }}
                          >
                            {formatNumberWithCommas(
                              periodAnalysisList.reduce(
                                (acc, cur) => acc + cur.commentCount,
                                0
                              )
                            )}
                            건
                          </TableText>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </ChartContainer>

            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 26,
              }}
            >
              {isShowInflow && (
                <>
                  {/* ********** 유입 사이트 ********** */}
                  <ChartContainer
                    style={{
                      width: 509,
                      minHeight: 392,
                    }}
                  >
                    <ChartHeader>
                      <ChartHeaderText>유입 사이트</ChartHeaderText>
                      <PretendardText
                        style={{
                          fontWeight: 500,
                          fontSize: "13px",
                          color: "#aaa",
                          lineHeight: "18.75px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate(route.statistics_inflow_site);
                          scrollToTop();
                        }}
                      >
                        더보기
                      </PretendardText>
                    </ChartHeader>
                    <table
                      style={{
                        width: "100%",
                        borderSpacing: 0,
                        borderCollapse: "collapse",
                      }}
                    >
                      <colgroup>
                        {/* 합계: 509px */}
                        {/* 도메인 */}
                        <col width="369px" />
                        {/* 클릭수 */}
                        <col width="140px" />
                      </colgroup>
                      <thead>
                        <tr
                          style={{
                            height: 41,
                            backgroundColor: "#313233",
                          }}
                        >
                          <th>
                            <TableText
                              style={{
                                fontWeight: "600",
                                lineHeight: "22.75px",
                                paddingLeft: 24,
                              }}
                            >
                              도메인
                            </TableText>
                          </th>
                          <th>
                            <TableText
                              style={{
                                fontWeight: "600",
                                lineHeight: "22.75px",
                                textAlign: "right",
                                marginRight: 24,
                              }}
                            >
                              클릭수
                            </TableText>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...Array(5)].map((_, index) => {
                          return (
                            <>
                              <tr
                                key={index}
                                style={{
                                  height: 59,
                                  borderBottom:
                                    index === 4
                                      ? "none"
                                      : "0.5px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <td>
                                  <TableText
                                    style={{
                                      maxWidth: 345,
                                      paddingLeft: 24,
                                    }}
                                  >
                                    www.naver.com
                                  </TableText>
                                </td>
                                <td>
                                  <TableText
                                    style={{
                                      textAlign: "right",
                                      marginRight: 24,
                                    }}
                                  >
                                    {/* {formatNumberWithCommas(2000)} */}n
                                  </TableText>
                                </td>
                              </tr>
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </ChartContainer>
                </>
              )}

              {/* ********** 많이 방문한 페이지 ********** */}
              {isShowMostVisited && (
                <ChartContainer
                  style={{
                    width: 509,
                    minHeight: 392,
                  }}
                >
                  <ChartHeader>
                    <ChartHeaderText>많이 방문한 페이지</ChartHeaderText>
                    <PretendardText
                      style={{
                        fontWeight: 500,
                        fontSize: "13px",
                        color: "#aaa",
                        lineHeight: "18.75px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        navigate(route.statistics_most_visited);
                        scrollToTop();
                      }}
                    >
                      더보기
                    </PretendardText>
                  </ChartHeader>
                  {isMostVisitLoading || isMostVisitError ? (
                    <div
                      style={{
                        width: "100%",
                        height: 336,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Loading color="#3a96df" style={{ position: "static" }} />
                    </div>
                  ) : (
                    <>
                      <table
                        style={{
                          width: "100%",
                          borderSpacing: 0,
                          borderCollapse: "collapse",
                        }}
                      >
                        <colgroup>
                          {/* 합계: 509px */}
                          {/* 제목 */}
                          <col width="214px" />
                          {/* URL */}
                          <col width="184px" />
                          {/* 조회 */}
                          <col width="111px" />
                        </colgroup>
                        <thead>
                          <tr
                            style={{
                              height: 41,
                              backgroundColor: "#313233",
                            }}
                          >
                            <th>
                              <TableText
                                style={{
                                  fontWeight: "600",
                                  lineHeight: "22.75px",
                                  paddingLeft: 24,
                                }}
                              >
                                제목
                              </TableText>
                            </th>
                            <th>
                              <TableText
                                style={{
                                  fontWeight: "600",
                                  lineHeight: "22.75px",
                                }}
                              >
                                URL
                              </TableText>
                            </th>
                            <th>
                              <TableText
                                style={{
                                  fontWeight: "600",
                                  lineHeight: "22.75px",
                                  textAlign: "right",
                                  marginRight: 24,
                                }}
                              >
                                조회
                              </TableText>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {mostVisitedData.map((item, index) => {
                            return (
                              <>
                                <tr
                                  key={index}
                                  style={{
                                    height: 59,
                                    borderBottom:
                                      index === 4
                                        ? "none"
                                        : "0.5px solid rgba(255, 255, 255, 0.12)",
                                  }}
                                >
                                  <td>
                                    <TableText
                                      style={{
                                        maxWidth: 124,
                                        paddingLeft: 24,
                                      }}
                                    >
                                      {item?.title || "Unknown"}
                                    </TableText>
                                  </td>
                                  <td>
                                    <TableText
                                      style={{
                                        maxWidth: 184,
                                      }}
                                    >
                                      {item?.pagePath || ""}
                                    </TableText>
                                  </td>
                                  <td>
                                    <TableText
                                      style={{
                                        textAlign: "right",
                                        marginRight: 24,
                                      }}
                                    >
                                      {formatNumberWithCommas(
                                        item?.pageViews || 0
                                      )}
                                    </TableText>
                                  </td>
                                </tr>
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                </ChartContainer>
              )}
            </div>
          </ContentContainer>
        </DetailPageLayout>
      </MainLayout>
    </>
  );
};

export default StatisticsHome;

const ChartHeaderText = styled(PretendardText).attrs((props) => {})`
  font-weight: 700;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: -0.3%;
  color: #f3f3f3;
`;

const ContentContainer = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

const ChartContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const ChartHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 30px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const ChartGraphWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  padding: 24px 30px;
`;
